import React, { useState, useEffect, useRef, useContext, useLayoutEffect } from "react";
import { ILastVisits } from "types/Recent";
import { useLocation } from "react-router-dom";
import { FormattedMessage } from "react-intl";
import { Row } from "react-bootstrap";
import { UserType } from "types/UserTypes";
import Cookies from "js-cookie";
import { capitalizeFirstLetter } from "helpers/commonHelpers";
import Search from "./components/common/Search";
import MobileNavigation from "./components/common/MobileNavigation";
import NavContext from "./components/common/NavContext";
import NavHeader from "./components/common/NavHeader";
import Recent from "./components/common/Recent";
import MenuItems from "./components/common/MenuItems";

interface INavigationProps {
  userType: UserType;
  isStaff: boolean;
}

const Navigation = function ({ userType, isStaff }: INavigationProps) {
  const { REACT_APP_ENVIRONMENT_NAME, REACT_APP_ENVIRONMENT_NAVBAR_COLOR } = import.meta.env;
  const ref = useRef(null);
  const { navigationConfig, setNavigationConfig } = useContext(NavContext);
  const location = useLocation();

  const [showMenuItem, setShowMenuItem] = useState<ILastVisits[]>([]);
  const [showRecent, setShowRecent] = useState<boolean>(false);
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [selectedSearch, setSelectedSearch] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [status, setStatus] = useState<boolean>(false);

  const shouldIndicateEnvironment = ["regression", "sandbox"].includes(REACT_APP_ENVIRONMENT_NAME || "");

  const getSuggestionResult = (value: ILastVisits[]) => {
    setShowMenuItem(value);
  };

  useEffect(() => {
    const updateMedia = () => {
      const screenWidth = window.innerWidth;
      setNavigationConfig((prev: any) => ({ ...prev, isMobile: screenWidth < 992, isShown: screenWidth >= 992 }));
    };
    updateMedia();
    window.addEventListener("resize", updateMedia);
    return () => window.removeEventListener("resize", updateMedia);
  }, []);

  useEffect(() => {
    if (location.pathname === "/") {
      setNavigationConfig((prev: any) => ({ ...prev, expanded: isExpanded }));
    }
  }, [isExpanded]);

  useEffect(() => {
    setNavigationConfig((prev: any) => ({ ...prev, expanded: false }));
  }, [navigationConfig.isMobile]);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      const { offsetWidth } = entry.target as HTMLElement;
      setNavigationConfig((prev: any) => ({ ...prev, navWidth: offsetWidth }));
    });
    observer.observe(el);

    return () => observer.disconnect();
  }, []);

  return (
    <>
      {navigationConfig.isMobile && (
        <MobileNavigation
          navigation={ref}
          hideButton={navigationConfig.isRequestFormV2Page && !navigationConfig.isRequestFormV2ReviewMode}
        />
      )}
      <nav
        ref={ref}
        className={`navigation hide-during-print ${navigationConfig.expanded && `is-expanded`} ${
          (showMenuItem.length > 0 ||
            (navigationConfig.isMobile && navigationConfig.isShown && !navigationConfig.isRequestPopupOpen)) &&
          `is-expanded-scroll`
        }  ${!navigationConfig.isShown && "d-none"}`}
        {...(shouldIndicateEnvironment &&
          REACT_APP_ENVIRONMENT_NAVBAR_COLOR && { style: { backgroundColor: REACT_APP_ENVIRONMENT_NAVBAR_COLOR } })}
      >
        {shouldIndicateEnvironment && (
          <div className="w-100 position-absolute font-weight-bold text-center mt-1" style={{ fontSize: 10 }}>
            {capitalizeFirstLetter(REACT_APP_ENVIRONMENT_NAME)}
          </div>
        )}
        <NavHeader />
        {Cookies.get("is_guest") !== "true" && (
          <Search
            getSuggestionResult={getSuggestionResult}
            setShowRecent={setShowRecent}
            setIsExpanded={setIsExpanded}
            setSelectedSearch={setSelectedSearch}
            selectedSearch={selectedSearch}
            setIsLoading={setIsLoading}
            setStatus={setStatus}
          />
        )}
        {navigationConfig.expanded && showRecent && !showMenuItem?.length && !isLoading && !status && (
          <Recent setSelectedSearch={setSelectedSearch} setShowRecent={setShowRecent} />
        )}

        {!showMenuItem?.length && !showRecent && !isLoading && !status && (
          <MenuItems setIsExpanded={setIsExpanded} userType={userType} isStaff={isStaff} />
        )}

        {isLoading ? (
          <div className="skeleton-card">
            <Row className="skeleton-row mx-md">
              <span className="skeleton" />
              <span className="skeleton" />
              <span className="skeleton" />
            </Row>
          </div>
        ) : (
          !showMenuItem?.length &&
          !isLoading &&
          status && (
            <p className="text-secondary">
              <FormattedMessage id="recent.noResultsFound" defaultMessage="No results found" />
            </p>
          )
        )}
      </nav>
    </>
  );
};

export default Navigation;
